import React, { useState, useRef, useEffect } from 'react';
import { useAuth, useLanguage, API_URL } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { Send, Loader2, Lightbulb, Copy, Trash2, Bot } from 'lucide-react';
import { Link } from 'react-router-dom';

// Render AI message with clickable links for app pages
const RenderAIMessage = ({ content }) => {
  if (!content) return null;
  const APP_PAGES = {
    '/dashboard': 'Tableau de bord', '/courses': 'Cours', '/grades': 'Notes',
    '/assignments': 'Devoirs & TP', '/schedule': 'Calendrier', '/messages': 'Messages',
    '/notifications': 'Notifications', '/analytics': 'Analyses', '/library': 'Bibliotheque',
    '/dictionary': 'Dictionnaire', '/transport': 'Transport', '/ai-assistant': 'JosAide',
    '/payments': 'Paiements', '/profile': 'Profil', '/universities': 'Universites',
    '/faculties': 'Facultes', '/departments': 'Departements', '/programs': 'Programmes',
    '/users': 'Utilisateurs', '/roles': 'Roles', '/registrations': 'Inscriptions',
    '/co-admins': 'Co-admins', '/legal': 'Mentions legales', '/deliberations': 'Deliberations',
    '/online-courses': 'Cours en Ligne', '/academic-year': 'Gestion Academique',
    '/study-groups': 'Groupes d\'etude', '/calls': 'Appels', '/referral': 'Parrainage',
    '/settings': 'Parametres', '/my-documents': 'Mes Dossiers', '/my-class': 'Ma Classe',
    '/class-groups': 'Gestion Classes', '/teacher-profile': 'Profil Enseignant',
    '/announcements': 'Annonces', '/organization': 'Organisation',
  };

  const renderPath = (path, trailing = '') => {
    if (APP_PAGES[path]) {
      return <><Link to={path} className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 rounded-md font-semibold text-xs hover:bg-blue-100 transition-colors">{APP_PAGES[path]}</Link>{trailing}</>;
    }
    return path + trailing;
  };

  const parts = content.split(/(\/[a-z][a-z0-9-]*(?:\s|[.,;:!?)]|$)|\*\*[^*]+\*\*|`[^`]+`|https?:\/\/[^\s]+)/gi);

  return (
    <div className="whitespace-pre-wrap text-sm leading-relaxed">
      {parts.map((part, i) => {
        if (!part) return null;
        if (part.startsWith('**') && part.endsWith('**')) {
          const innerText = part.slice(2, -2);
          const boldPathMatch = innerText.match(/^(\/[a-z][a-z0-9-]*)([\s.,;:!?)]?)$/i);
          if (boldPathMatch && APP_PAGES[boldPathMatch[1]]) return <span key={i}>{renderPath(boldPathMatch[1], boldPathMatch[2] || '')}</span>;
          return <strong key={i} className="font-semibold">{innerText}</strong>;
        }
        if (part.startsWith('`') && part.endsWith('`')) {
          const innerText = part.slice(1, -1);
          const codePathMatch = innerText.match(/^(\/[a-z][a-z0-9-]*)([\s.,;:!?)]?)$/i);
          if (codePathMatch && APP_PAGES[codePathMatch[1]]) return <span key={i}>{renderPath(codePathMatch[1], codePathMatch[2] || '')}</span>;
          return <code key={i} className="bg-gray-200 px-1 rounded text-sm">{innerText}</code>;
        }
        if (/^https?:\/\//.test(part)) return <a key={i} href={part} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">{part}</a>;
        const pathMatch = part.match(/^(\/[a-z][a-z0-9-]*)([\s.,;:!?)]?)$/i);
        if (pathMatch && APP_PAGES[pathMatch[1]]) return <span key={i}>{renderPath(pathMatch[1], pathMatch[2] || '')}</span>;
        return <span key={i}>{part}</span>;
      })}
    </div>
  );
};

export const AIAssistantPage = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const messagesEndRef = useRef(null);
  const fr = language === 'fr';

  const scrollToBottom = () => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  useEffect(() => { scrollToBottom(); }, [messages]);

  useEffect(() => {
    loadSuggestions();
    loadHistory();
  }, []);

  const loadSuggestions = async () => {
    try {
      const res = await axios.get(`${API_URL}/ai/suggestions`);
      if (res.data.suggestions) setSuggestions(res.data.suggestions);
    } catch (err) { console.error(err); }
  };

  const loadHistory = async () => {
    try {
      const res = await axios.get(`${API_URL}/ai/history`);
      if (res.data.messages?.length) {
        setMessages(res.data.messages.map(m => ({ role: m.role, content: m.content })));
      }
    } catch (err) { console.error(err); }
  };

  const sendMessage = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || loading) return;

    setMessages(prev => [...prev, { role: 'user', content: text }]);
    setInput('');
    setLoading(true);

    try {
      const res = await axios.post(`${API_URL}/ai/chat`, { message: text, context: 'general' });
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
    } catch (error) {
      console.error('AI Error:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: fr ? "Desole, une erreur s'est produite. Veuillez reessayer." : "Sorry, an error occurred.",
        error: true
      }]);
    } finally {
      setLoading(false);
    }
  };

  const clearChat = async () => {
    setMessages([]);
    try {
      await axios.delete(`${API_URL}/api/ai/history`);
    } catch (e) { /* silent */ }
    toast.success(fr ? 'Conversation effacee' : 'Chat cleared');
  };

  const copyMessage = (content) => {
    navigator.clipboard.writeText(content);
    toast.success(fr ? 'Copie !' : 'Copied!');
  };

  return (
    <div className="h-[calc(100vh-180px)] flex flex-col" data-testid="ai-assistant-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-[#0F2C59] to-[#007BFF] rounded-xl flex items-center justify-center shadow-lg">
            <Bot className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-heading font-bold text-slate-900" data-testid="ai-title">
              Assistant Intellectuel JosAide
            </h1>
            <p className="text-xs text-slate-400">
              {fr ? 'Votre guide personnel dans JoSmart Uni' : 'Your personal guide in JoSmart Uni'}
            </p>
          </div>
        </div>
        <Button variant="ghost" size="icon" onClick={clearChat} data-testid="ai-clear-chat" className="text-slate-400 hover:text-red-500">
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>

      {/* Chat Area */}
      <div className="flex-1 flex flex-col bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-sm">
        <ScrollArea className="flex-1 p-4">
          {messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center p-8">
              <div className="w-20 h-20 bg-gradient-to-br from-[#0F2C59]/10 to-[#007BFF]/10 rounded-2xl flex items-center justify-center mb-6">
                <Bot className="w-10 h-10 text-[#007BFF]" />
              </div>
              <h3 className="text-lg font-heading font-bold text-slate-800 mb-2" data-testid="ai-welcome">
                {fr ? 'Bonjour ! Je suis JosAide' : 'Hello! I\'m JosAide'}
              </h3>
              <p className="text-slate-400 mb-6 max-w-md text-sm">
                {fr
                  ? "Je suis votre guide dans l'application JoSmart Uni. Demandez-moi ou trouver une fonctionnalite, comment naviguer, ou comment utiliser l'app."
                  : "I'm your guide in JoSmart Uni. Ask me where to find a feature, how to navigate, or how to use the app."}
              </p>
              <div className="grid grid-cols-2 gap-2 w-full max-w-lg" data-testid="ai-suggestions">
                {suggestions.slice(0, 6).map((s, i) => (
                  <button
                    key={i}
                    onClick={() => setInput(s.prompt)}
                    className="flex items-center gap-2 p-3 rounded-xl border border-slate-100 hover:border-[#007BFF]/30 hover:bg-blue-50/50 transition-all text-left group"
                    data-testid={`ai-suggestion-${i}`}
                  >
                    <Lightbulb className="w-4 h-4 text-amber-500 flex-shrink-0" />
                    <span className="text-xs text-slate-600 group-hover:text-slate-800 line-clamp-2">{s.label}</span>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {messages.map((msg, idx) => (
                <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className="max-w-[85%]">
                    {msg.role === 'assistant' && (
                      <div className="flex items-center gap-2 mb-1.5">
                        <div className="w-6 h-6 bg-gradient-to-br from-[#0F2C59] to-[#007BFF] rounded-full flex items-center justify-center">
                          <Bot className="w-3.5 h-3.5 text-white" />
                        </div>
                        <span className="text-xs font-semibold text-slate-500">JosAide</span>
                      </div>
                    )}
                    <div className={`rounded-2xl px-4 py-3 ${
                      msg.role === 'user'
                        ? 'bg-[#0F2C59] text-white rounded-br-md'
                        : msg.error
                        ? 'bg-red-50 text-red-800 border border-red-200'
                        : 'bg-slate-50 text-slate-800 rounded-bl-md border border-slate-100'
                    }`}>
                      {msg.role === 'assistant'
                        ? <RenderAIMessage content={msg.content} />
                        : <div className="whitespace-pre-wrap text-sm">{msg.content}</div>
                      }
                    </div>
                    {msg.role === 'assistant' && !msg.error && (
                      <Button variant="ghost" size="sm" className="h-6 px-2 text-xs text-slate-400 hover:text-slate-600 mt-1" onClick={() => copyMessage(msg.content)}>
                        <Copy className="w-3 h-3 mr-1" /> {fr ? 'Copier' : 'Copy'}
                      </Button>
                    )}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="max-w-[85%]">
                    <div className="flex items-center gap-2 mb-1.5">
                      <div className="w-6 h-6 bg-gradient-to-br from-[#0F2C59] to-[#007BFF] rounded-full flex items-center justify-center">
                        <Bot className="w-3.5 h-3.5 text-white" />
                      </div>
                      <span className="text-xs font-semibold text-slate-500">JosAide</span>
                    </div>
                    <div className="bg-slate-50 rounded-2xl rounded-bl-md px-4 py-3 border border-slate-100">
                      <div className="flex items-center gap-2">
                        <div className="flex gap-1">
                          <span className="w-2 h-2 bg-[#007BFF] rounded-full animate-bounce" style={{animationDelay: '0ms'}} />
                          <span className="w-2 h-2 bg-[#007BFF] rounded-full animate-bounce" style={{animationDelay: '150ms'}} />
                          <span className="w-2 h-2 bg-[#007BFF] rounded-full animate-bounce" style={{animationDelay: '300ms'}} />
                        </div>
                        <span className="text-xs text-slate-400">{fr ? 'Reflexion...' : 'Thinking...'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          )}
        </ScrollArea>

        {/* Input */}
        <div className="p-4 border-t border-slate-100 bg-white">
          <form onSubmit={sendMessage} className="flex gap-2">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={fr ? 'Posez votre question sur JoSmart Uni...' : 'Ask about JoSmart Uni...'}
              className="flex-1 h-11 rounded-xl border-slate-200 focus:border-[#007BFF]"
              disabled={loading}
              data-testid="ai-input"
            />
            <Button
              type="submit"
              disabled={!input.trim() || loading}
              className="h-11 px-5 rounded-xl bg-[#0F2C59] hover:bg-[#0F2C59]/90 gap-2"
              data-testid="ai-send-button"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              <span className="hidden sm:inline">{fr ? 'Envoyer' : 'Send'}</span>
            </Button>
          </form>
          <p className="text-[10px] text-slate-300 text-center mt-2">
            {fr ? 'JosAide repond uniquement aux questions sur JoSmart Uni' : 'JosAide only answers questions about JoSmart Uni'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default AIAssistantPage;
